import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from 'components/Link';
import { get } from 'lodash';
import TabContainer from './TabContainer';
import CircularProgress from '@mui/material/CircularProgress';
import { ISchema } from '../dialogs/CreateResourceModal';
import FormContainer from './FormContainer';
import { useTranslation } from 'react-i18next';

interface IActionItem {
  label: string;
  onClick: () => void | Promise<void>;
  color?: 'primary' | 'error';
  disabled?: boolean;
  hide?: () => boolean;
  confirmMessage?: string;
}

export interface ITab<TAttributes> {
  id: string;
  title: string;
  hide?: () => boolean;
  render?: (resource: TAttributes) => JSX.Element;
  getUiSchema?: () => ISchema;
  actions?: (resource: TAttributes) => IActionItem[];
}

interface IUpdateAttributes {
  [key: string]: string;
}

const PREFIX = 'ResourceDetailContainer';

const classes = {
  root: `${PREFIX}-root`,
  actions: `${PREFIX}-actions`,
};

const Root = styled('div')(() => ({
  position: 'relative',
  width: '100%',
}));

interface IProps<TAttributes> {
  breadcrumbText: string;
  breadcrumbUrl: string;
  currentTabId: string;
  tabs: ITab<TAttributes>[];
  onFetch: () => Promise<TAttributes>;
  onUpdate?: (attrs: IUpdateAttributes) => Promise<TAttributes>;
  onTabChanged?: (tabId: string) => void;
  onGenerateActions?: (resource: TAttributes) => IActionItem[];
  displayNameKey?: string;
}

export default function ResourceDetailContainer<TAttributes>(props: IProps<TAttributes>) {
  const {
    breadcrumbText,
    breadcrumbUrl,
    tabs,
    currentTabId,
    onFetch,
    onUpdate,
    onTabChanged,
    displayNameKey,
  } = props;

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TAttributes>();

  const handleUpdate = async (attrs: IUpdateAttributes) => {
    if (onUpdate) {
      setLoading(true); // Indicate loading during update
      try {
        const updated = await onUpdate(attrs);
        const normalizedUpdated = { ...updated };
        if (normalizedUpdated['userRole'] === 'SME') {
          normalizedUpdated['userRole'] = 'Business';
        }
        setResult(normalizedUpdated);
      } catch (error) {
        console.error('Error updating resource:', error);
        // Optionally show an error message to the user
      } finally {
        setLoading(false);
      }
    } else {
      console.error('Update is not supported');
    }
  };

  useEffect(() => {
    let isMounted = true; // Add a flag to track component mount status

    const loadResources = async () => {
      setLoading(true);
      try {
        const data = await onFetch();
        const normalizedData = { ...data };
        if (normalizedData['userRole'] === 'SME') {
          normalizedData['userRole'] = 'Business';
        }
        if (isMounted) {
          setResult(normalizedData);
        }
      } catch (error) {
        console.error('Error fetching resource:', error);
        // Optionally show an error message to the user
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void loadResources();

    return () => {
      isMounted = false; // Set the flag to false when the component unmounts
    };
  }, []);

  const handleGenerateTabActions = (actions: IActionItem[]) => {
    const filteredActions = actions.filter((action) => !action.hide || !action.hide());
    return (
      <Stack spacing={2} direction="row" sx={{ mb: 2, justifyContent: 'flex-end' }}>
        {filteredActions.map((action, index) => {
          const { label, onClick, color, confirmMessage, disabled } = action;
          return (
            <Link
              disabled={disabled}
              isAsync
              button
              color={color}
              key={index}
              onClick={onClick}
              text={label}
              confirmMessage={confirmMessage}
            />
          );
        })}
      </Stack>
    );
  };

  const handleGenerateTabContent = (tab: ITab<TAttributes>) => {
    const { getUiSchema, render } = tab;
    if (!result) {
      return null;
    }

    // this is a detail tab
    if (getUiSchema) {
      return <FormContainer attributes={result} schema={getUiSchema()} onSave={handleUpdate} />;
      // this is a custom tab
    } else if (render) {
      return render(result);
    } else {
      return <></>;
    }
  };

  const handleGenerateTab = (tab: ITab<TAttributes>) => {
    const actions = tab.actions && result ? tab.actions(result) : [];
    return (
      <div>
        {actions.length > 0 && handleGenerateTabActions(actions)}
        {handleGenerateTabContent(tab)}
      </div>
    );
  };

  const { t } = useTranslation('default');

  return (
    <Root className={classes.root}>
      {loading ? (
        <CircularProgress size={20} />
      ) : (
        <>
          {/* <IconButton aria-label="back" onClick={() => history.go(-1)}>
            <ArrowBackIos />
          </IconButton> */}
          <Breadcrumbs aria-label="breadcrumb">
            <Link to="/" text={t('home')} />
            <Link to={breadcrumbUrl} text={breadcrumbText} />
            <Typography color="text.primary">
              {get(result, displayNameKey || 'name') || '-'}
            </Typography>
          </Breadcrumbs>
          <br />
          <TabContainer
            onGenerateTab={handleGenerateTab}
            tabs={tabs}
            currentTabId={currentTabId}
            onTabChanged={onTabChanged}
          />
        </>
      )}
    </Root>
  );
}
